import { useEventListener } from '@/composables/useEventListener'
import { useUiStore } from '@/stores/uiStore'
import {
  isCloseRouteModeShortcut,
  isRedoRouteModeShortcut,
  isUndoRouteModeShortcut,
  shouldIgnoreRouteModeShortcut,
} from '@/utils/routeModeShortcuts'

interface UseRouteModeShortcutsOptions {
  isTrackDragging: () => boolean
  cancelTrackDragging: () => void
  canUndoTrackDrawing: () => boolean
  undoTrackDrawing: () => Promise<void> | void
  canRedoTrackDrawing: () => boolean
  redoTrackDrawing: () => Promise<void> | void
  closeTrackDrawing: () => Promise<void> | void
  canUndoTrackEdit: () => boolean
  undoTrackEdit: () => Promise<void> | void
  canRedoTrackEdit: () => boolean
  redoTrackEdit: () => Promise<void> | void
  closeTrackEdit: () => Promise<void> | void
}

export function useRouteModeShortcuts(options: UseRouteModeShortcutsOptions) {
  const uiStore = useUiStore()

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isCloseRouteModeShortcut(event) && !shouldIgnoreRouteModeShortcut(event, false)) {
      if (uiStore.isTrackDrawingActive) {
        event.preventDefault()
        void options.closeTrackDrawing()
        return
      }

      if (uiStore.isTrackEditMode) {
        event.preventDefault()
        void options.closeTrackEdit()
        return
      }
    }

    if (shouldIgnoreRouteModeShortcut(event, uiStore.isDialogOpen)) return

    if (event.key === 'Escape') {
      if (options.isTrackDragging()) {
        event.preventDefault()
        options.cancelTrackDragging()
        return
      }

      if (uiStore.isTrackDrawingActive) {
        event.preventDefault()
        void options.closeTrackDrawing()
        return
      }

      if (uiStore.isTrackEditMode) {
        event.preventDefault()
        void options.closeTrackEdit()
      }

      return
    }

    if (
      uiStore.isTrackDrawingActive &&
      isUndoRouteModeShortcut(event) &&
      options.canUndoTrackDrawing()
    ) {
      event.preventDefault()
      void options.undoTrackDrawing()
      return
    }

    if (
      uiStore.isTrackDrawingActive &&
      isRedoRouteModeShortcut(event) &&
      options.canRedoTrackDrawing()
    ) {
      event.preventDefault()
      void options.redoTrackDrawing()
      return
    }

    if (uiStore.isTrackEditMode && isUndoRouteModeShortcut(event) && options.canUndoTrackEdit()) {
      event.preventDefault()
      void options.undoTrackEdit()
      return
    }

    if (uiStore.isTrackEditMode && isRedoRouteModeShortcut(event) && options.canRedoTrackEdit()) {
      event.preventDefault()
      void options.redoTrackEdit()
      return
    }
  }

  useEventListener(document, 'keydown', handleKeyDown)
}
